import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import * as Sentry from "@sentry/react";
import Index from "@/pages/Index";
import Login from "@/pages/Login";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import Unsubscribe from "@/pages/Unsubscribe";
import { ApiKeyManager } from "@/components/security/ApiKeyManager";
import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Initialize Sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || "https://eecfff8ca124a7bed6d39b5843cd7ca6@o4508566835691520.ingest.de.sentry.io/4508566857384016",
  integrations: [
    new Sentry.BrowserTracing({
      tracePropagationTargets: ["localhost", "your-production-domain.com"],
    }),
  ],
  tracesSampleRate: 1.0,
  enabled: import.meta.env.PROD,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount === 2) {
          Sentry.captureException(error);
        }
        return failureCount < 3;
      },
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth error:', error);
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: "Please sign in again.",
          });
          setIsAuthenticated(false);
          return;
        }

        setIsAuthenticated(!!session);

        if (!session) {
          const { data: { session: refreshedSession }, error: refreshError } = 
            await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Session refresh error:', refreshError);
            setIsAuthenticated(false);
            return;
          }
          
          setIsAuthenticated(!!refreshedSession);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        toast({
          title: "Signed out",
          description: "You have been signed out.",
        });
      } else if (event === 'SIGNED_IN') {
        setIsAuthenticated(true);
        toast({
          title: "Signed in",
          description: "Welcome back!",
        });
      } else if (event === 'TOKEN_REFRESHED') {
        setIsAuthenticated(true);
      }
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, [toast]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-6 text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-4">Page Not Found</h2>
        <p className="text-muted-foreground mb-4">The page you're looking for doesn't exist.</p>
        <button
          onClick={() => window.location.href = '/'}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider delayDuration={0}>
          <Helmet>
            <title>EV Newsletter Platform</title>
            <meta name="description" content="Stay updated with the latest EV industry news, trends, and insights through our curated newsletter platform." />
            <meta name="keywords" content="EV news, electric vehicles, newsletter, industry insights, automotive news" />
            <meta property="og:title" content="EV Newsletter Platform" />
            <meta property="og:description" content="Stay updated with the latest EV industry news, trends, and insights through our curated newsletter platform." />
            <meta property="og:type" content="website" />
            <meta property="og:image" content="/og-image.png" />
            <meta name="twitter:card" content="summary_large_image" />
            <link rel="canonical" href="https://your-domain.com" />
          </Helmet>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                      <Index />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

const ErrorFallback = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-6 text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-4">Something went wrong</h2>
        <p className="text-muted-foreground mb-4">Our team has been notified and is working to fix the issue.</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
        >
          Try again
        </button>
      </div>
    </div>
  );
};

export default App;
